import {
  AccountTypes,
  NizzaProductDataSource,
  NizzaProductDataSourceConfig,
  NizzaProductRequestOriginTypes,
  getNizza,
} from '@nizza/core';
import { reaction, when } from 'mobx';
import baseLogger from '~logger';
import {
  PlatformProductDataSource,
  VtexProductDataSource,
} from './data-sources';

const logger = baseLogger.child('productDataSourceFactory');

function createDataSource(
  config: NizzaProductDataSourceConfig,
): NizzaProductDataSource {
  const { account } = getNizza();
  const { accountType } = account!;
  const { origin } = config;

  const isVtex = [
    AccountTypes.VTEX,
    AccountTypes.VTEX_CMS,
    AccountTypes.VTEX_IO,
  ].includes(accountType);

  if (accountType === AccountTypes.PLATFORM) {
    return new PlatformProductDataSource(config);
  }

  if (
    [
      NizzaProductRequestOriginTypes.GLOBAL_PAGE,
      NizzaProductRequestOriginTypes.UNKNOWN,
    ].includes(origin as NizzaProductRequestOriginTypes) &&
    isVtex
  ) {
    return new VtexProductDataSource({ ...config, corsProxy: true });
  }

  return new VtexProductDataSource({ ...config });
}

/**
 * Synchronously creates a product data source. Assumes that `nizza.account`
 * is already defined.
 *
 * @param {NizzaProductDataSourceConfig} config - The configuration object for creating the data source.
 * @returns {NizzaProductDataSource} The created product data source.
 * @throws {Error} Throws an error if `nizza.account` is not defined.
 */
export function createProductDataSource(
  config: NizzaProductDataSourceConfig,
): NizzaProductDataSource {
  const nizza = getNizza();
  if (!nizza.account) throw new Error('Nizza account is not defined');
  return createDataSource(config);
}

/**
 * Asynchronously creates a product data source. Waits until `nizza.account` is defined.
 * While waiting, it monitors `accountStatus` changes and logs an error if the status changes to 'error'.
 *
 * @param {NizzaProductDataSourceConfig} config - The configuration object for creating the data source.
 * @returns {Promise<NizzaProductDataSource>} A promise that resolves with the created product data source.
 */
export async function createProductDataSourceAsync(
  config: NizzaProductDataSourceConfig,
): Promise<NizzaProductDataSource> {
  const nizza = getNizza();

  // Monitor accountStatus changes and log error if it becomes 'error'
  const disposer = reaction(
    () => nizza.accountStatus,
    status => {
      if (status === 'error') {
        logger.error(
          `Account status changed to 'error' and product data source could not be created`,
        );
      }
    },
  );

  await when(() => !!nizza.account);
  // Dispose the reaction once the account is defined
  disposer();

  return createDataSource(config);
}
